/**
 * Il vocabolario della riga del tabellone: come si chiamano gli stati, di che
 * colore sono, quando una riga è fuori gioco.
 *
 * Estratto da `CarichiPrevistiView` quando il calendario è diventato la seconda
 * vista sulle stesse righe (#39). Se «Sollecitare» si chiamasse in due modi nei
 * due posti, sarebbero due tabelloni — ed è esattamente ciò che stiamo evitando.
 */

import { cn } from "@/lib/utils";
import type { CaricoPrevistoRow, StatoCaricoPrevisto } from "@/types/api";

/**
 * La ricerca su una riga del tabellone: cliente, NDP, commesse, riferimenti del
 * cliente. Sta qui perché la usano in due — la tabella e il calendario — e
 * devono cercare **sugli stessi campi**: due ricerche che trovano cose diverse
 * sullo stesso dato sono peggio di una ricerca sola.
 *
 * I riferimenti cliente sono la ragione per cui esiste: cercare «26IT0992» o
 * «PO 18» trova righe che col solo nome del cliente non si troverebbero.
 */
export function cercaRighe(items: CaricoPrevistoRow[], q: string): CaricoPrevistoRow[] {
  const t = q.trim().toLowerCase();
  if (!t) return items;
  return items.filter((r) =>
    [r.cliente_nome, r.ndp_numero ?? "", r.commesse.join(" "), r.riferimenti_cliente.join(" ")]
      .join(" ")
      .toLowerCase()
      .includes(t),
  );
}

export const STATO_LABEL: Record<StatoCaricoPrevisto, string> = {
  in_attesa: "In attesa",
  confermato: "Confermato",
  posticipato: "Posticipato",
  interlocutoria: "Interlocutoria",
  sollecito_dovuto: "Sollecitare",
  ndp_emessa: "NDP emessa",
  allineato_ddt: "Allineato DDT",
  partito: "Partito",
};

export const STATO_CLASSE: Record<StatoCaricoPrevisto, string> = {
  in_attesa: "bg-blue-100 text-blue-700",
  confermato: "bg-emerald-100 text-emerald-700",
  posticipato: "bg-amber-100 text-amber-700",
  interlocutoria: "bg-amber-100 text-amber-700",
  sollecito_dovuto: "bg-red-100 text-red-700",
  ndp_emessa: "bg-slate-100 text-slate-600",
  allineato_ddt: "bg-slate-100 text-slate-600",
  partito: "bg-emerald-100 text-emerald-800",
};

/** La riga non è più in gioco: la merce è uscita. */
export function eUscita(stato: StatoCaricoPrevisto): boolean {
  return stato === "partito";
}

/** Il tempo cliente si congela alla NDP, non alla partenza. */
export function eCongelato(stato: StatoCaricoPrevisto): boolean {
  return stato === "ndp_emessa" || stato === "allineato_ddt" || stato === "partito";
}

export const NDP_LABEL: Record<string, string> = {
  emessa: "Emessa",
  non_emessa: "Non emessa",
  non_condivisa: "Non condivisa",
};

export function StatoBadge({ stato }: { stato: StatoCaricoPrevisto }) {
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", STATO_CLASSE[stato])}>
      {STATO_LABEL[stato]}
    </span>
  );
}
